// FichajeFormEmpleado.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import Clook from "../../components/Clock";
import { AlertCircle } from "lucide-react";

export default function FichajeFormEmpleado() {
  const { token, loading, user } = useContext(AuthContext);
  const [fichajes, setFichajes] = useState([]);
  const [generalError, setGeneralError] = useState("");
  const [notification, setNotification] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const [modalOpen, setModalOpen] = useState(false);
  const [incidenciaComentario, setIncidenciaComentario] = useState("");
  const [incidenciaTipo, setIncidenciaTipo] = useState("incidencia");
  const [validationErrors, setValidationErrors] = useState({});
  const [incidenciaFichajeId, setIncidenciaFichajeId] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setFilterDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  const parseDateTimeLocal = (fecha, hora) => {
    if (!fecha) return null;
    const dtStr = hora ? `${fecha}T${hora}` : `${fecha}T00:00:00`;
    const dt = new Date(dtStr);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const segundosAHMS = (segundos) => {
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = segundos % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const loadFichajes = () => {
    return api.get("/fichajes/mios", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setFichajes(res.data.data || []); return res.data.data || []; })
      .catch(() => { setGeneralError("Error al cargar los fichajes."); return []; });
  };

  useEffect(() => { if (!token) return; loadFichajes(); }, [token]);

  const getAddress = async (lat, lon) => {
    try {
      const res = await api.get("https://nominatim.openstreetmap.org/reverse",
        { params: { lat, lon, format: "json" }, headers: { "Accept-Language": "es" } });
      const addr = res.data.address || {};
      return { ubicacion: addr.road || addr.neighbourhood || "Desconocida", ciudad: addr.city || addr.town || addr.village || "Desconocida" };
    } catch {
      return { ubicacion: "Desconocida", ciudad: "Desconocida" };
    }
  };

  const ultimaEntrada = [...fichajes].filter(f => f.tipo === "entrada").reverse().find(f => {
    const salidaPosterior = fichajes.find(s => s.tipo === "salida" && parseDateTimeLocal(s.fecha, s.hora_salida) > parseDateTimeLocal(f.fecha, f.hora_entrada));
    return !salidaPosterior;
  });

  const ultimoDescanso = [...fichajes].filter(f => f.tipo === "descanso").reverse().find(f => !f.hora_fin_descanso);

  const enviarFichaje = async (tipo, geoData = {}) => {
    let endpoint = "";
    if (tipo === "entrada") endpoint = "/fichajes/startEntrada";
    else if (tipo === "descanso") {
      if (!ultimaEntrada) return setNotification("No puedes iniciar descanso sin una entrada abierta.");
      endpoint = ultimoDescanso ? "/fichajes/endDescanso" : "/fichajes/startDescanso";
    } else if (tipo === "salida") {
      if (!ultimaEntrada) return setNotification("No puedes fichar salida sin una entrada abierta.");
      if (ultimoDescanso) return setNotification("Cierra el descanso antes de fichar salida.");
      endpoint = "/fichajes/salida";
    }
    try {
      await api.post(endpoint, { usuario_id: user.id, empresa_id: user.empresa_id, ...geoData }, { headers: { Authorization: `Bearer ${token}` } });
      await loadFichajes();
      setPage(1);
      setNotification(`Fichaje de ${tipo} registrado`);
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      setGeneralError(err.response?.data?.error || "Error al registrar el fichaje.");
    }
  };

  const handleFichar = async (tipo) => {
    if (!token) return setNotification("Debes iniciar sesión primero");
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    if (tipo === "entrada" && ultimaEntrada) {
      setNotification("Ya tienes una entrada activa. Refresca la página.");
      submittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    try {
      if (!navigator.geolocation) {
        setNotification("Tu navegador no soporta geolocalización. Se fichará sin ubicación.");
        await enviarFichaje(tipo);
      } else {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              try {
                const { latitude, longitude } = position.coords;
                const { ubicacion, ciudad } = await getAddress(latitude, longitude);
                await enviarFichaje(tipo, { ubicacion, ciudad, latitud: latitude, longitud: longitude });
                resolve();
              } catch (err) { reject(err); }
            },
            async () => {
              setNotification("Ubicación no disponible. Se fichará sin ubicación.");
              await enviarFichaje(tipo);
              resolve();
            },
            { timeout: 8000 }
          );
        });
      }
    } catch {
      setGeneralError("Error inesperado en el proceso de fichaje.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const openModal = () => { setIncidenciaComentario(""); setIncidenciaTipo("incidencia"); setIncidenciaFichajeId(""); setValidationErrors({}); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  const handleCrearIncidencia = async () => {
    if (!token) return setNotification("Debes iniciar sesión primero");
    if (!incidenciaComentario.trim()) return setNotification("Debes escribir un comentario");
    try {
      setValidationErrors({});
      await api.post("/incidencias", { comentario: incidenciaComentario, tipo: incidenciaTipo, fichaje_id: incidenciaFichajeId || null }, { headers: { Authorization: `Bearer ${token}` } });
      setNotification("Incidencia creada correctamente");
      setTimeout(() => setNotification(""), 4000);
      closeModal();
      loadFichajes();
    } catch (err) {
      if (err.response?.status === 422) setValidationErrors(err.response.data.errors || {});
      else setGeneralError(err.response?.data?.error || "Error al crear la incidencia.");
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>
      Cargando sesión…
    </div>
  );

  const fichajesFiltrados = filterDate ? fichajes.filter(f => f.fecha === filterDate) : fichajes;
  const paginatedFichajes = fichajesFiltrados.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(fichajesFiltrados.length / itemsPerPage);
  const totalSegundosUI = fichajes.filter(f => f.duracion !== null).reduce((acc, f) => acc + f.duracion, 0);
  const totalHorasUI = segundosAHMS(totalSegundosUI);

  const TIPO_CONFIG = {
    entrada:  { label: "Entrada",  color: "var(--clr-accent)", bg: "var(--clr-accent-bg)", btnClass: "tn-btn--primary" },
    descanso: { label: "Descanso", color: "#F59E0B", bg: "#FFFBEB", btnClass: "tn-btn--ghost" },
    salida:   { label: "Salida",   color: "#EF4444", bg: "#FEF2F2", btnClass: "tn-btn--danger" },
  };

  return (
    <div>
      {/* Reloj + estado */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <h1 className="tn-page-title" style={{ marginBottom: 4 }}>Control de fichajes</h1>
        <Clook />
      </div>

      {notification && <div className="tn-alert tn-alert--info" style={{ marginBottom: 16, textAlign: "center" }}>{notification}</div>}
      {generalError && <div className="tn-alert tn-alert--danger" style={{ marginBottom: 16 }}>{generalError}</div>}

      {/* Botones de fichaje */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 28 }}>
        {["entrada", "descanso", "salida"].map((tipo) => {
          const tieneEntradaActiva = !!ultimaEntrada;
          const tieneDescansoActivo = !!ultimoDescanso;
          const isActive = (tipo === "entrada" && tieneEntradaActiva) || (tipo === "descanso" && tieneDescansoActivo);
          let isButtonDisabled = isSubmitting;
          if (tipo === "entrada" && tieneEntradaActiva) isButtonDisabled = true;
          if (tipo === "descanso" && !tieneEntradaActiva) isButtonDisabled = true;
          if (tipo === "salida" && (!tieneEntradaActiva || tieneDescansoActivo)) isButtonDisabled = true;

          const cfg = TIPO_CONFIG[tipo];

          return (
            <div key={tipo} className={`tn-fichar-card${isActive ? " active-card" : ""}${isButtonDisabled && !isActive ? " disabled-card" : ""}`}>
              <div className="tn-fichar-icon" style={{ background: cfg.bg }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: cfg.color, opacity: isButtonDisabled && !isActive ? 0.5 : 1 }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--clr-text)", marginBottom: 4 }}>
                {tipo === "descanso" && tieneDescansoActivo ? "Fin descanso" : cfg.label}
                {isActive && <span className="tn-status-badge" style={{ marginLeft: 8 }}>Activo</span>}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--clr-muted)", marginBottom: 16 }}>
                {tipo === "entrada" ? "Iniciar jornada" : tipo === "descanso" ? "Iniciar / Finalizar descanso" : "Terminar jornada"}
              </div>
              <button
                className={`tn-btn ${cfg.btnClass}`}
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => handleFichar(tipo)}
                disabled={isButtonDisabled}
              >
                {isSubmitting ? "Procesando…" : (tipo === "descanso" && tieneDescansoActivo ? "Finalizar descanso" : cfg.label)}
              </button>
            </div>
          );
        })}
      </div>

      {/* Filtros + nueva incidencia */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <input type="date" className="tn-input" style={{ width: "auto" }} value={filterDate}
          onChange={e => { setFilterDate(e.target.value); setPage(1); }} />
        <button className="tn-btn tn-btn--ghost" onClick={() => setFilterDate("")}>Limpiar filtro</button>
        <button className="tn-btn tn-btn--ghost" style={{ marginLeft: "auto" }} onClick={openModal}>
          <AlertCircle size={16} /> Nueva incidencia
        </button>
      </div>

      <div style={{ fontSize: 13, color: "var(--clr-muted)", marginBottom: 12 }}>
        Total horas: <strong>{totalHorasUI}</strong>
      </div>

      {/* Tabla */}
      <div className="tn-card">
        <div className="tn-table-wrap">
          <table className="tn-table">
            <thead>
              <tr><th>Tipo</th><th>Fecha</th><th>Hora</th><th>Ciudad</th><th>Duración</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {paginatedFichajes.length > 0 ? paginatedFichajes.map(f => {
                const dt = f.tipo === "entrada" ? parseDateTimeLocal(f.fecha, f.hora_entrada)
                  : f.tipo === "salida" ? parseDateTimeLocal(f.fecha, f.hora_salida)
                  : parseDateTimeLocal(f.fecha, f.hora_entrada_descanso);
                const fechaFormateada = dt ? dt.toLocaleDateString("es-ES") : "";
                const horaFormateada = dt ? dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
                const estado = (f.tipo === "entrada" && f.hora_salida) ? "Registrado"
                  : (f.tipo === "descanso" && f.hora_fin_descanso) ? "Finalizado" : "";
                return (
                  <tr key={f.id}>
                    <td data-label="Tipo" style={{ textTransform: "capitalize" }}>{f.tipo}</td>
                    <td data-label="Fecha">{fechaFormateada}</td>
                    <td data-label="Hora">{horaFormateada}</td>
                    <td data-label="Ciudad">{f.ciudad || "Desconocida"}</td>
                    <td data-label="Duración">{f.duracion ? segundosAHMS(f.duracion) : "—"}</td>
                    <td data-label="Estado">{estado && <span className="tn-badge tn-badge--green">{estado}</span>}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="6" style={{ textAlign: "center", padding: "40px 20px", color: "var(--clr-muted)" }}>No hay fichajes</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="tn-pagination" style={{ padding: "12px 16px", borderTop: "1px solid var(--clr-border)" }}>
            <button className="tn-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} className={`tn-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
            ))}
            <button className="tn-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Modal incidencia */}
      {modalOpen && (
        <div className="tn-modal-backdrop" onClick={closeModal}>
          <div className="tn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tn-modal__header">
              <span style={{ fontWeight: 700, fontSize: 15 }}>Nueva incidencia</span>
              <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--clr-muted)", fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div className="tn-modal__body">
              {Object.keys(validationErrors).length > 0 && (
                <div className="tn-alert tn-alert--danger" style={{ marginBottom: 14 }}>
                  {Object.values(validationErrors).map((msgs, i) => <div key={i}>{msgs.join(", ")}</div>)}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label className="tn-label">Tipo de incidencia</label>
                  <select className="tn-input tn-select" value={incidenciaTipo} onChange={e => setIncidenciaTipo(e.target.value)}>
                    <option value="incidencia">Incidencia</option>
                    <option value="falta">Falta</option>
                    <option value="retraso">Retraso</option>
                  </select>
                </div>
                <div>
                  <label className="tn-label">Asociar a fichaje (opcional)</label>
                  <select className="tn-input tn-select" value={incidenciaFichajeId} onChange={e => setIncidenciaFichajeId(e.target.value)}>
                    <option value="">— No asociar —</option>
                    {fichajes.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.tipo} - {f.fecha} {f.hora_entrada || f.hora_salida || f.hora_entrada_descanso}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="tn-label">Descripción</label>
                  <textarea className="tn-input tn-textarea" rows="4" placeholder="Describe la incidencia…"
                    value={incidenciaComentario} onChange={e => setIncidenciaComentario(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="tn-modal__footer">
              <button className="tn-btn tn-btn--ghost" onClick={closeModal}>Cancelar</button>
              <button className="tn-btn tn-btn--primary" onClick={handleCrearIncidencia}>Crear incidencia</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
